<script>
/**
 * ResponsiveToolbar
 * ---------------------------------------------------------------------------
 * Barra de filtros + acciones que se reparte en una fila en desktop y se
 * APILA en móvil/tablet, reemplazando los patrones repetidos de
 * `row > col-sm-12 col-md-2/3` que se aprietan en pantallas pequeñas.
 *
 * Dos slots:
 *   - `filters` (slot por defecto): inputs / selects / buscadores.
 *   - `actions`: botones de acción (Nuevo, Exportar, etc.).
 *
 * En <= md, los slots se apilan verticalmente y los controles ocupan el
 * ancho completo para facilitar el toque. Es solo presentacional.
 *
 * Props:
 *   stackAt  -> breakpoint en que apila ('sm' | 'md' | 'lg'). Default 'md'.
 *   align    -> alineación vertical en fila ('center' | 'start' | 'end').
 *
 * Uso:
 *   <ResponsiveToolbar>
 *     <b-form-input ... />              // va al slot de filtros
 *     <template #actions>
 *       <b-button>Nuevo</b-button>
 *     </template>
 *   </ResponsiveToolbar>
 */
export default {
  name: "ResponsiveToolbar",
  props: {
    stackAt: {
      type: String,
      default: "md",
      validator: (v) => ["sm", "md", "lg"].includes(v),
    },
    align: {
      type: String,
      default: "center",
      validator: (v) => ["center", "start", "end"].includes(v),
    },
  },
  computed: {
    hasActions() {
      return !!this.$slots.actions || !!this.$scopedSlots.actions;
    },
  },
};
</script>

<template>
  <div class="rwd-toolbar" :class="[`rwd-toolbar--stack-${stackAt}`, `rwd-toolbar--align-${align}`]">
    <div class="rwd-toolbar__filters">
      <slot />
    </div>
    <div v-if="hasActions" class="rwd-toolbar__actions">
      <slot name="actions" />
    </div>
  </div>
</template>

<style scoped>
.rwd-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  width: 100%;
}
.rwd-toolbar__filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  flex: 1 1 auto;
  min-width: 0;
}
.rwd-toolbar__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}
.rwd-toolbar--align-center { align-items: center; }
.rwd-toolbar--align-start { align-items: flex-start; }
.rwd-toolbar--align-end { align-items: flex-end; }

/* Apilado en pantallas pequeñas: controles a ancho completo. */
.rwd-toolbar--stack-md { /* default */ }

@media (max-width: 767.98px) {
  .rwd-toolbar--stack-md {
    flex-direction: column;
    align-items: stretch;
  }
  .rwd-toolbar--stack-md .rwd-toolbar__filters,
  .rwd-toolbar--stack-md .rwd-toolbar__actions {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }
}
@media (max-width: 575.98px) {
  .rwd-toolbar--stack-sm {
    flex-direction: column;
    align-items: stretch;
  }
  .rwd-toolbar--stack-sm .rwd-toolbar__filters,
  .rwd-toolbar--stack-sm .rwd-toolbar__actions {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }
}
@media (max-width: 991.98px) {
  .rwd-toolbar--stack-lg {
    flex-direction: column;
    align-items: stretch;
  }
  .rwd-toolbar--stack-lg .rwd-toolbar__filters,
  .rwd-toolbar--stack-lg .rwd-toolbar__actions {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }
}
</style>
